import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import {
  LocatorInput,
  OBJECT_TYPES,
  ObjectMappingEntryView,
  ObjectMappingLocator,
} from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { isValidObjectName } from '../object-names';
import { LocatorKind, isRoleValue, kindLabelKey, locatorKindsFor, readLocator, toLocator } from '../locator-kinds';
import { technologyKey } from '../technology';
import { isNameTaken, serverMessage } from '../../../shared/http/server-message';

/** Wie `ObjectMappingDtos.TEXT_MAX` im BE: Kandidatenwert. */
const TEXT_MAX = 2000;
/** Wie `LocatorRules.MAX_CANDIDATES` im BE. */
const MAX_CANDIDATES = 20;
/** Genug Treffer fuer den Vergleich ohne Gross-/Kleinschreibung; der Server sucht nach Namensanfang. */
const NAME_CHECK_SIZE = 20;
/** Kuerzel-Empfehlung je Typ wie im Bundle (`OB_KUERZEL`). */
const PREFIX_BY_TYPE: Readonly<Record<string, string>> = {
  Button: 'b',
  Eingabe: 'e',
  Link: 'l',
  Bild: 'i',
  Text: 't',
  Auswahl: 's',
};
const NAME_FORMAT = /[\s.]/;

/** `label` ist ein i18n-Schluessel, ausser bei einer unbekannten Strategie aus dem Bestand. */
interface KindOption {
  value: string;
  label: string;
  translated: boolean;
}

interface LocatorRow {
  key: number;
  /** Art der Technologie oder, fuer Bestand ohne Art im Dialog, die gespeicherte Strategie. */
  choice: string;
  value: string;
  /** Bestand ausserhalb der Liste der Technologie: bleibt nur an dieser Zeile waehlbar. */
  stock: KindOption | null;
  /** Gespeicherte Strategie ohne Art im Dialog (etwa `label`); wird unveraendert zurueckgeschrieben. */
  rawStrategy: string | null;
  /** Erst nach einer Eingabe zeigt ein leerer Wert den Hinweis; gesperrt ist Speichern trotzdem. */
  touched: boolean;
}

type NameError = 'nameRequired' | 'nameFormat' | 'invalidName' | 'nameTaken';

/**
 * Eintrag eines Testelements anlegen oder bearbeiten (NEX-1705, NEX-2451), Design
 * `page-objektbibliothek.jsx:80-140`. Ohne `entry` wird angelegt; beim Bearbeiten bleibt der Name fest
 * (Umbenennen hat eigenen Dialog). Die Arten der Kandidaten richten sich nach der Engine (`locator-kinds`).
 */
@Component({
  selector: 'app-object-entry-dialog',
  imports: [TranslatePipe, MatFormField, MatLabel, MatInput, MatIcon, MatButton, ConfirmDialogComponent],
  templateUrl: './object-entry-dialog.component.html',
  styleUrl: './object-entry-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectEntryDialogComponent implements OnInit {
  private readonly service = inject(ObjectMappingService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly mappingId = input.required<string>();
  readonly entry = input<ObjectMappingEntryView | null>(null);
  /** Name des Testelements fuer „Im Testschritt: {Testelement}.{Name}“. */
  readonly libraryName = input<string>('');
  readonly engineId = input<string | null>(null);
  /** Vorbelegter Name beim Anlegen, etwa aus einer offenen Referenz (NEX-2454). */
  readonly presetName = input<string>('');

  readonly saved = output<ObjectMappingEntryView>();
  readonly cancelled = output<void>();

  readonly nameMax = FIELD_LENGTHS.NAME_MAX;
  readonly textMax = TEXT_MAX;
  readonly maxCandidates = MAX_CANDIDATES;

  readonly name = signal('');
  readonly typeName = signal('');
  readonly area = signal('');
  readonly rows = signal<LocatorRow[]>([]);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  private readonly nameTouched = signal(false);
  private readonly takenName = signal<string | null>(null);

  readonly creating = computed(() => !this.entry());
  readonly kinds = computed(() => locatorKindsFor(this.engineId()));
  readonly kindOptions = computed<KindOption[]>(() =>
    this.kinds().map((kind) => ({ value: kind, label: kindLabelKey(kind), translated: true })),
  );
  readonly technologyLabelKey = computed(() => technologyKey(this.engineId() ?? 'browser'));
  readonly typeOptions = computed(() => {
    const current = this.entry()?.typeName;
    const types: string[] = [...OBJECT_TYPES];
    return current && !types.includes(current) ? [...types, current] : types;
  });
  readonly prefix = computed(() => PREFIX_BY_TYPE[this.typeName()] ?? null);
  readonly nameExample = computed(() => `${this.prefix() ?? 'b'}Anmelden`);
  readonly prefixExample = computed(() => {
    const name = this.name().trim();
    const prefix = this.prefix();
    if (!this.creating() || !prefix || !name || name.startsWith(prefix)) return null;
    return { prefix, example: prefix + name.charAt(0).toUpperCase() + name.slice(1) };
  });
  readonly stepName = computed(() => (this.creating() ? this.name().trim() : (this.entry()?.logicalName ?? '')) || '…');
  readonly nameTaken = computed(() => {
    const taken = this.takenName();
    return taken !== null && taken.toLowerCase() === this.name().toLowerCase();
  });
  readonly nameError = computed<NameError | null>(() => {
    if (!this.creating() || !this.nameTouched()) return null;
    const name = this.name();
    if (!name.trim()) return 'nameRequired';
    if (NAME_FORMAT.test(name)) return 'nameFormat';
    if (!isValidObjectName(name)) return 'invalidName';
    return this.nameTaken() ? 'nameTaken' : null;
  });
  readonly showValueMissing = computed(() => this.rows().some((row) => row.touched && !row.value.trim()));
  readonly showRoleHint = computed(() => this.rows().some((row) => this.isInvalidRole(row)));
  readonly tooLong = computed(() =>
    this.rows().some((row) => {
      const locator = this.toInput(row);
      return locator.value.length > TEXT_MAX || (locator.name?.length ?? 0) > this.nameMax;
    }),
  );
  readonly noCandidates = computed(() => this.rows().length === 0);
  readonly fromFulltag = computed(() => this.noCandidates() && !!this.entry()?.fulltag);
  /** Ohne Fulltag braucht ein Eintrag mindestens einen Kandidaten (A4). */
  readonly canRemove = computed(() => this.rows().length > 1 || !!this.entry()?.fulltag);
  readonly canSave = computed(
    () =>
      !this.saving() &&
      (!this.creating() || (isValidObjectName(this.name()) && !this.nameTaken())) &&
      !!this.typeName() &&
      this.rows().every((row) => !!row.value.trim() && !this.isInvalidRole(row)) &&
      !this.tooLong() &&
      (!this.noCandidates() || this.fromFulltag()),
  );

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private nextKey = 0;
  private nameCheck: Subscription | null = null;
  /** Name, fuer den die Pruefung laeuft bzw. zuletzt geantwortet hat (auch mit Fehler). */
  private checkingName: string | null = null;
  private checkedName: string | null = null;
  /** Das BE vergleicht exakt; ohne Antwort der Pruefung koennte `BANMELDEN` neben `bAnmelden` landen. */
  private saveAfterCheck = false;

  constructor() {
    afterNextRender(() => this.nameInput()?.nativeElement.focus());
  }

  ngOnInit(): void {
    const entry = this.entry();
    this.name.set(entry ? entry.logicalName : this.presetName());
    this.typeName.set(entry?.typeName ?? '');
    this.area.set(entry?.area ?? '');
    this.rows.set(entry ? entry.locators.map((locator) => this.toRow(locator)) : [this.newRow()]);
  }

  rowOptions(row: LocatorRow): KindOption[] {
    return row.stock ? [...this.kindOptions(), row.stock] : this.kindOptions();
  }

  onName(event: Event): void {
    this.name.set(inputValue(event));
    this.error.set(null);
  }

  onNameBlur(): void {
    this.nameTouched.set(true);
    this.checkName();
  }

  onType(event: Event): void {
    this.typeName.set(inputValue(event));
    this.error.set(null);
  }

  onArea(event: Event): void {
    this.area.set(inputValue(event));
    this.error.set(null);
  }

  onKind(index: number, event: Event): void {
    this.updateRow(index, { choice: inputValue(event) });
  }

  onValue(index: number, event: Event): void {
    this.updateRow(index, { value: inputValue(event), touched: true });
  }

  addRow(): void {
    if (this.rows().length >= MAX_CANDIDATES) return;
    this.rows.update((rows) => [...rows, this.newRow()]);
    this.error.set(null);
  }

  removeRow(index: number): void {
    if (!this.canRemove()) return;
    this.rows.update((rows) => rows.filter((_, i) => i !== index));
    this.error.set(null);
  }

  /** Tauscht die Zeile mit ihrer Nachbarin; `step` ist -1 (hoch) oder 1 (runter). */
  move(index: number, step: -1 | 1): void {
    const target = index + step;
    const rows = [...this.rows()];
    if (target < 0 || target >= rows.length) return;
    [rows[index], rows[target]] = [rows[target], rows[index]];
    this.rows.set(rows);
    this.error.set(null);
  }

  save(): void {
    if (!this.canSave()) return;
    if (this.creating() && this.checkedName !== this.name()) {
      this.nameTouched.set(true);
      this.saving.set(true);
      this.saveAfterCheck = true;
      if (this.checkingName !== this.name()) this.checkName();
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const fields = {
      typeName: this.typeName(),
      area: this.area().trim() || null,
      locators: this.rows().map((row) => this.toInput(row)),
    };
    const entry = this.entry();
    const request = entry
      ? this.service.updateEntry(this.projectId(), this.mappingId(), entry.id, {
          ...fields,
          // Die Adresse am Eintrag hat im Dialog kein Feld mehr (A3); ein gespeicherter Wert bleibt erhalten.
          ...(entry.url ? { url: entry.url } : {}),
        })
      : this.service.createEntry(this.projectId(), this.mappingId(), { logicalName: this.name(), ...fields });
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (view) => {
        this.saving.set(false);
        this.saved.emit(view);
      },
      error: (err: unknown) => {
        this.error.set(this.messageFor(err));
        this.saving.set(false);
      },
    });
  }

  /** Ein Fehler der Pruefung sperrt nichts; der Name gilt dann als frei. */
  private checkName(): void {
    const name = this.name();
    if (!this.creating() || !isValidObjectName(name) || name === this.checkingName || name === this.checkedName) return;
    this.nameCheck?.unsubscribe();
    this.checkingName = name;
    this.nameCheck = this.service
      .entries(this.projectId(), this.mappingId(), name, 0, NAME_CHECK_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const lower = name.toLowerCase();
          if (page.content.some((entry) => entry.logicalName.toLowerCase() === lower)) this.takenName.set(name);
          this.onNameChecked(name);
        },
        error: () => this.onNameChecked(name),
      });
  }

  private onNameChecked(name: string): void {
    this.checkingName = null;
    this.checkedName = name;
    if (!this.saveAfterCheck) return;
    this.saveAfterCheck = false;
    this.saving.set(false);
    if (name === this.name()) this.save();
  }

  private isInvalidRole(row: LocatorRow): boolean {
    return row.choice === 'ROLE' && !!row.value.trim() && !isRoleValue(row.value);
  }

  private toInput(row: LocatorRow): LocatorInput {
    if (row.rawStrategy !== null && row.choice === row.rawStrategy) return { strategy: row.choice, value: row.value };
    return toLocator(row.choice as LocatorKind, row.value, this.engineId());
  }

  private updateRow(index: number, change: Partial<LocatorRow>): void {
    this.rows.update((rows) => rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
    this.error.set(null);
  }

  private newRow(): LocatorRow {
    return { key: this.nextKey++, choice: this.kinds()[0], value: '', stock: null, rawStrategy: null, touched: false };
  }

  /** Das BE kennt nur `strategy`, `value` und `name`; weitere Classic-Schluessel fallen beim Speichern weg. */
  private toRow(locator: ObjectMappingLocator): LocatorRow {
    const read = readLocator(locator, this.engineId());
    const row = { key: this.nextKey++, value: read.value, touched: false };
    if (read.kind) {
      const stock = this.kinds().includes(read.kind) ? null : { value: read.kind, label: read.label, translated: true };
      return { ...row, choice: read.kind, stock, rawStrategy: null };
    }
    const strategy = locator['strategy'] ?? '';
    const stock = { value: strategy, label: read.label, translated: read.label !== strategy };
    return { ...row, choice: strategy, stock, rawStrategy: strategy };
  }

  private messageFor(err: unknown): string {
    if (isNameTaken(err)) return this.translate.instant('objectMappings.entryDialog.nameTaken');
    return serverMessage(err) ?? this.translate.instant('objectMappings.entryDialog.error');
  }
}

function inputValue(event: Event): string {
  return (event.target as HTMLInputElement | HTMLSelectElement).value;
}
